import { backendUrl, tenantId, setTenantIdentity, applyRuntimeEnv } from './envConfig';

/**
 * Resolves the current tenant from the hostname via the BFF.
 * Called once before React mounts.
 *
 * Flow:
 * 1. Calls GET /api/v1/tenant/visual-config — the BFF middleware reads the
 *    Origin header and resolves the tenant from *_DOMAIN env vars.
 * 2. On success: updates tenantId/tenantName via setTenantIdentity() and
 *    caches the visual config in localStorage for useVisualConfig.
 * 3. On failure: falls back to REACT_APP_TENANT_ID env var (build-time).
 */
export async function resolveTenant() {
  try {
    const response = await fetch(`${backendUrl}/api/v1/tenant/visual-config`);

    if (!response.ok) {
      throw new Error(`visual-config responded ${response.status}`);
    }

    const data = await response.json();
    const { tenantId: resolvedId, tenantName: resolvedName } = data;

    if (resolvedId && resolvedName) {
      setTenantIdentity(resolvedId, resolvedName);
      console.info(`[resolveTenant] Resolved tenant: ${resolvedName} (${resolvedId})`);
    }

    // Apply tenant-specific env config from BFF (pre-login values)
    applyRuntimeEnv(data);

    // Resolve relative asset URLs against the BFF origin
    const resolved = { ...data };
    ['logoUrl', 'logoLoginUrl', 'faviconUrl', 'themeUrl'].forEach((key) => {
      if (resolved[key] && resolved[key].startsWith('/')) {
        resolved[key] = `${backendUrl}${resolved[key]}`;
      }
    });

    // Inject theme CSS as inline <style> before React mounts to prevent FOUC
    if (resolved.themeUrl) {
      try {
        const cssResponse = await fetch(resolved.themeUrl);
        if (cssResponse.ok) {
          const css = await cssResponse.text();
          let style = document.querySelector("style[data-tenant-theme]");
          if (!style) {
            style = document.createElement('style');
            style.setAttribute('data-tenant-theme', 'true');
            document.head.appendChild(style);
          }
          style.textContent = css;
        }
      } catch (err) {
        console.warn('[resolveTenant] Theme CSS fetch failed', err);
      }
    }

    // Pre-cache visual config so useVisualConfig can read it immediately
    if (resolvedId) {
      const cacheKey = `dashboard_visual_config_${resolvedId}`;
      localStorage.setItem(cacheKey, JSON.stringify(resolved));
    }
  } catch (err) {
    if (tenantId) {
      console.warn(`[resolveTenant] BFF resolve failed, using env var fallback: ${err.message}`);
    } else {
      console.error(`[resolveTenant] Cannot resolve tenant and no env var fallback: ${err.message}`);
    }
  }
}
